import { deleteCustomers } from './api';
import { ConfirmWindow, WRITES_ENABLED } from '@/components/MaintenanceForm';
import { Button } from '@/components/Button';
import b from '@/components/BatchSelection.module.css';
import { useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate } from 'react-router';
import { useFormFactor } from '@/lib/formFactor';
import { useSession } from '@/stores/session';
import { Tag, Dot } from '@/components/Tag';
import { Empty } from '@/components/States';
import { date } from '@/lib/format';
import { AppliedChips, CardList, FilterButton, ListBody, ListScaffold, Pager, SearchBox, Segments, ViewChips } from '@/features/products/kit/List';
import { FilterSheet } from '@/features/products/kit/Sheet';
import { DataTable, Two, type Col } from '@/features/products/kit/Table';
import { useInfinite, usePagedList, useUrlState, withParam } from '@/features/products/kit/hooks';
import { CustomerCard } from './Cards';
import { CustomerDetail } from './CustomerDetail';
import { usePartnerSegments } from './nav';
import { activeState, customerProfile, invoiceType, settlementText } from './status';
import type { Customer, CustomerStats } from './types';

/** 视图 = 老接口 status；老接口不传时只看正常客户，老页面默认传 all（src/app/(app)/customers/page.tsx） */
const VIEWS: { key: string; label: string; count: (st: CustomerStats) => number }[] = [
  { key: 'all', label: '全部客户', count: (st) => st.all },
  { key: 'active', label: '正常', count: (st) => st.active },
  { key: 'inactive', label: '停用', count: (st) => st.inactive },
];
const PROFILE_COUNT: Record<string, (st: CustomerStats) => number> = {
  complete: (st) => st.complete,
  missingContact: (st) => st.missingContact,
  missingPhone: (st) => st.missingPhone,
  missingAddress: (st) => st.missingAddress,
};

export function CustomersPage() {
  const can = useSession((st) => st.can);
  if (!can('customers.read', 'taobao.manage')) {
    return can('suppliers.read') ? <Navigate to="/suppliers" replace /> : <Empty icon="lock" title="没有查看客户资料的权限">需要「客户 · 查看」权限，请联系管理员。</Empty>;
  }
  return <CustomerList />;
}

function CustomerList() {
  const ff = useFormFactor();
  const nav = useNavigate();
  const can = useSession((st) => st.can);
  const segments = usePartnerSegments();
  const [sp, set] = useUrlState();
  const view = VIEWS.find((v) => v.key === sp.get('view')) || VIEWS[0];
  const profile = sp.get('profile') || undefined;
  const q = sp.get('q') || '';
  const selectedId = sp.get('id') || undefined;
  const [selected, setSelected] = useState<string[]>([]);
  const [deleting, setDeleting] = useState(false);
  const [selectionMode, setSelectionMode] = useState(false);
  const [panel, setPanel] = useState(false);
  const [sentinel, setSentinel] = useState<HTMLDivElement | null>(null);

  const desk = ff === 'desktop';
  const query = useMemo(() => ({ status: view.key, profile, keyword: q || undefined }), [view, profile, q]);
  const list = usePagedList<Customer, CustomerStats>('/api/customers', query, desk ? 30 : 20, !desk);
  useInfinite(sentinel, !desk, list.loadMore, list.items.length);
  const stats = list.stats;
  const isTaobaoOnly = can('taobao.manage') && !can('customers.read');
  const canDelete = can('customers.delete') || isTaobaoOnly;
  useEffect(() => { setSelected([]); setSelectionMode(false); }, [view.key, profile, q, list.page]);
  const toggle = (id: string) => setSelected((ids) => ids.includes(id) ? ids.filter((item) => item !== id) : [...ids, id]);
  const batchControls = selectionMode && <div className={b.toolbar}><span>已选 {selected.length} 个</span><button type="button" onClick={() => setSelected(list.items.slice(0, 100).map((item) => item.id))}>全选已加载</button><button type="button" onClick={() => setSelected([])}>清空</button><button type="button" disabled={!WRITES_ENABLED || !selected.length || selected.length > 100} onClick={() => setDeleting(true)}>删除所选</button></div>;
  const canCreate = can('customers.create') || isTaobaoOnly;

  const cols: Col<Customer>[] = [
    ...(selectionMode ? [{ key: 'selection', title: '选择', w: 50, render: (c: Customer) => <input type="checkbox" className={b.check} checked={selected.includes(c.id)} aria-label={`选择客户 ${c.name}`} onClick={(event) => event.stopPropagation()} onChange={() => toggle(c.id)} /> }] : []),
    { key: 'name', title: '客户名称', w: 200, fix: true, render: (c) => <b>{c.name}</b> },
    { key: 'contact', title: '联系人 / 电话', w: 160, render: (c) => <Two top={c.contact || <span className="em">—</span>} sub={c.phone || undefined} /> },
    { key: 'addr', title: '地址', w: 280, render: (c) => c.address || <span className="em">—</span> },
    { key: 'owner', title: '归属业务员', w: 100, render: (c) => c.ownerUser?.name || <span className="em">未分配</span> },
    { key: 'stl', title: '结算', w: 200, render: (c) => { const x = settlementText(c); return <Dot tone={x.dot}>{x.label}</Dot>; } },
    { key: 'inv', title: '默认开票', w: 110, render: (c) => (c.defaultInvoiceType ? invoiceType[c.defaultInvoiceType] || c.defaultInvoiceType : <span className="em">—</span>) },
    { key: 'st', title: '状态', w: 72, render: (c) => { const st = activeState(c.isActive); return st ? <Tag tone={st.tone} sm>{st.label}</Tag> : <span className="em">正常</span>; } },
    { key: 'note', title: '备注', w: 220, render: (c) => c.note || <span className="em">—</span> },
    { key: 'upd', title: '更新', w: 110, render: (c) => <span className="tnum em">{date(c.updatedAt)}</span> },
  ];

  const filtered = !!(q || profile);
  const empty = (
    <Empty icon="team" title={filtered ? '没有符合条件的客户' : `「${view.label}」里没有客户`}>
      {filtered ? '换个关键词，或者清空筛选再看看。' : '换个视图看看。'}
    </Empty>
  );
  const common = { state: { loading: list.loading, error: list.error, count: list.items.length }, errorTitle: '客户列表加载失败', detail: 'GET /api/customers', onRetry: list.reload, empty };

  return (
    <ListScaffold
      title="客户 / 供应商"
      total={list.total}
      actions={canDelete && <Button disabled={!WRITES_ENABLED} onClick={() => { setSelectionMode((value) => !value); setSelected([]); }}>{selectionMode ? '取消选择' : '批量删除'}</Button>}
      segments={<Segments items={segments} />}
      chips={<ViewChips items={VIEWS.map((v) => ({ key: v.key, label: v.label, count: stats ? v.count(stats) : undefined }))} value={view.key} onChange={(k) => set({ view: k === 'all' ? undefined : k, id: undefined })} />}
      filterBtn={<FilterButton count={profile ? 1 : 0} onClick={() => setPanel(true)} />}
      applied={<AppliedChips items={profile ? [{ key: 'profile', label: customerProfile[profile] || profile }] : []} onRemove={() => set({ profile: undefined })} onClear={() => set({ profile: undefined })} />}
      search={<SearchBox value={q} placeholder="客户名称 / 联系人 / 电话 / 地址 / 税号" onSubmit={(v) => set({ q: v, id: undefined })} />}
      searching={!!q}
      create={canCreate ? { href: '/customers/new', local: true, label: '新建客户' } : null}
      onRefresh={list.reload}
      list={
        <ListBody {...common}>
          {batchControls}
          <CardList bar={<span>{list.total} 个客户{q && ` · 搜索「${q}」`}</span>} hasNext={list.hasNext} loading={list.loading} count={list.items.length} sentinel={setSentinel}>
            {list.items.map((c) => (
              <div key={c.id} className={b.row}>{selectionMode && <label><input type="checkbox" className={b.check} checked={selected.includes(c.id)} aria-label={`选择客户 ${c.name}`} onChange={() => toggle(c.id)} /></label>}<CustomerCard c={c} selected={ff === 'tablet' && c.id === selectedId} to={ff === 'tablet' ? `/customers?${withParam(sp, 'id', c.id)}` : `/customers/${c.id}`} /></div>
            ))}
          </CardList>
        </ListBody>
      }
      desk={
        <ListBody {...common}>
          {batchControls}
          <DataTable cols={cols} rows={list.items} onRow={(c) => nav(`/customers/${c.id}`)} />
        </ListBody>
      }
      pager={desk && list.items.length > 0 ? <Pager page={list.page} size={list.pageSize} count={list.items.length} total={list.total} hasNext={list.hasNext} onGo={list.goto} /> : undefined}
      detail={
        ff === 'tablet'
          ? selectedId
            ? <CustomerDetail key={selectedId} id={selectedId} embedded />
            : <Empty icon="team" title="选择左侧客户查看资料">联系方式、结算、开票和最近订单在这里打开。</Empty>
          : undefined
      }
      overlay={<>{deleting && <ConfirmWindow title={`彻底删除 ${selected.length} 个客户？`} confirm="确认删除" disabled={!WRITES_ENABLED} onClose={() => setDeleting(false)} onConfirm={async () => { await deleteCustomers(selected); setDeleting(false); setSelected([]); list.reload(); }}><p>仅允许删除没有业务关联的客户。有订单、结算或其他关联时整批拒绝删除，客户资料不可恢复。</p></ConfirmWindow>}{panel && (
        <FilterSheet
          value={{ profile }}
          groups={[{ key: 'profile', title: '资料状态', opts: Object.entries(customerProfile).map(([value, label]) => ({ value, label, count: stats ? PROFILE_COUNT[value](stats) : undefined })) }]}
          onClose={() => setPanel(false)}
          onApply={(v) => { setPanel(false); set({ profile: v.profile, id: undefined }); }}
        />
      )}</>}
    />
  );
}
